import React, { useState, useEffect, useRef } from 'react';
import { useFilePicker } from '../../hooks/useFilePicker';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Trash2, FileText, Camera, Star, Upload, ImagePlus } from 'lucide-react';
import RoomService from '../../services/RoomService';
import HouseService from '../../services/HouseService';
import { toast, ConfirmationModal, LoadingState, ErrorState } from '../../components/v2/V2Components';
import { PLACEHOLDER_IMAGES } from '../../constants/images';
import { MAX_IMAGE_UPLOAD_MB } from '../../constants/uploads';
import { ROOM_FIELD_IDS } from '../../constants/formFieldIds';
import { FALLBACK_FLOOR_LEVELS, reservedPropertyKeyError } from '../../constants/roomFormOptions';
import { sanitizeInput } from '../../utils/validation';
import { uploadErrorMessage } from '../../utils/uploadErrors';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const MAX_ROOM_IMAGES = 10;
// Shared cap (constants/uploads.js) mirroring the backend per-image limit.
const MAX_IMAGE_SIZE_MB = MAX_IMAGE_UPLOAD_MB;
// Mirrors the server allowlist (utils/file_helpers.py IMAGE_EXTENSIONS):
// HEIC/HEIF are accepted and converted to JPEG server-side — rejecting them
// here bounced every iPhone library photo for no reason.
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];

const withImportedOption = (options, currentValue) => {
  if (!currentValue) return options;
  const exists = options?.find(opt => opt.value === currentValue);
  if (!exists) {
      return [...(options || []), { value: currentValue, label: `${currentValue} (Legacy)` }];
  }
  return options;
};

const RoomEdit = () => {
  usePageView(ANALYTICS_EVENTS.SPACES_ROOM_FORM_VIEWED, { is_edit_mode: true });
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Inline save feedback — a toast alone disappears before the user finds
  // the offending field.
  const [nameError, setNameError] = useState(null);
  const [saveErrors, setSaveErrors] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    floor: '',
    house_id: '',
    properties: {}
  });

  const [configOptions, setConfigOptions] = useState({
    floor_levels: []
  });

  const [houses, setHouses] = useState([]);
  // Whether the room arrived unanchored, captured once at load. The picker's
  // visibility keys off THIS rather than the live formData.house_id: reading
  // the value the <select> writes would unmount the field the instant a
  // single-house user picked a house (see RoomEdit.test.jsx regression).
  const [loadedWithoutHouse, setLoadedWithoutHouse] = useState(false);

  const [notes, setNotes] = useState('');

  // Image state
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [activeImageMenu, setActiveImageMenu] = useState(null);
  // Image delete goes through ConfirmationModal per CLAUDE.md — never
  // window.confirm (native dialogs also freeze headless/E2E browsers).
  const [deleteImageCandidate, setDeleteImageCandidate] = useState(null);
  const [deletingImage, setDeletingImage] = useState(false);
  // Synchronous in-flight latch: ConfirmationModal calls onClose in the same
  // tick as onConfirm (before the deletingImage state flips), so a ref is the
  // only thing that can keep the dialog open until the delete settles.
  // Same pattern as HouseFloorPlanEditor's deletingPlanRef.
  const deletingImageRef = useRef(false);
  const [loadError, setLoadError] = useState(null);

  // Property state
  const [newPropKey, setNewPropKey] = useState('');
  const [newPropValue, setNewPropValue] = useState('');

  useEffect(() => {
    fetchRoom();
    // fetchRoom closes over `id`; re-running on id change is the intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchRoom = async () => {
    try {
      setLoading(true);

      // Fetch room data, config, and houses concurrently. allSettled so a
      // houses failure degrades to a hidden picker rather than blocking the
      // whole edit page.
      const results = await Promise.allSettled([
        RoomService.getRoom(id),
        RoomService.getConfig(),
        HouseService.getHouses()
      ]);

      if (results[0].status === 'rejected') {
          throw results[0].reason;
      }

      const data = results[0].value;

      // Fall back to the shared static list on config failure — previously
      // this defaulted to [] and left the floor dropdown empty (AddRoom
      // already had the fallback; the two forms had drifted).
      let config = { floor_levels: FALLBACK_FLOOR_LEVELS };
      if (results[1].status === 'fulfilled') {
          config = results[1].value;
      } else {
          console.warn('Failed to fetch room config', results[1].reason);
      }

      setConfigOptions({
        floor_levels: config.floor_levels || []
      });

      setHouses(
        results[2].status === 'fulfilled' && Array.isArray(results[2].value)
          ? results[2].value
          : []
      );

      setLoadedWithoutHouse(!data.house_id);

      const props = data.properties || {};
      setFormData({
        name: data.name || '',
        floor: data.floor || '1st Floor',
        // Legacy rooms predating house-anchoring have house_id null; they
        // hydrate to '' and the submit handler drops the key entirely.
        house_id: data.house_id ? String(data.house_id) : '',
        properties: props
      });
      setNotes(props.notes || '');
      setImages(data.images || []);
    } catch (error) {
      console.error("Failed to fetch room:", error);
      // Show an in-place error with Retry instead of bouncing to the
      // details page (which is likely failing for the same reason).
      setLoadError(error.message || 'Failed to load room details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const addProperty = () => {
    // Reserved keys ('notes' has a dedicated field; 'dimensions' is retired
    // and filtered from the list, so it'd be invisible and unremovable) —
    // shared guard with AddRoom.
    const reservedError = reservedPropertyKeyError(newPropKey);
    if (reservedError) {
      toast.error(reservedError);
      return;
    }
    if (newPropKey.trim() && newPropValue.trim()) {
      setFormData(prev => ({
        ...prev,
        properties: {
          ...prev.properties,
          [sanitizeInput(newPropKey.trim(), 100)]: sanitizeInput(newPropValue.trim(), 255)
        }
      }));
      setNewPropKey('');
      setNewPropValue('');
    }
  };

  const removeProperty = (key) => {
    setFormData(prev => {
      const newProps = { ...prev.properties };
      delete newProps[key];
      return { ...prev, properties: newProps };
    });
  };

  // Receives a FileList directly from useFilePicker (the hook resets the
  // underlying input's value so the same file can be re-selected next time).
  const handleImageUpload = async (files) => {
    const fileList = Array.from(files || []);
    if (fileList.length === 0) return;

    // How many slots remain
    const slotsAvailable = MAX_ROOM_IMAGES - images.length;
    const filesToUpload = fileList.slice(0, slotsAvailable);

    if (fileList.length > slotsAvailable) {
      toast.error(`Only ${slotsAvailable} image slot${slotsAvailable === 1 ? '' : 's'} remaining — uploading first ${filesToUpload.length}`);
    }

    // Validate all files before starting
    const validFiles = [];
    for (const file of filesToUpload) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" is not a supported image type`);
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        toast.error(`"${file.name}" exceeds ${MAX_IMAGE_SIZE_MB}MB limit`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: validFiles.length });

    let successCount = 0;
    for (let i = 0; i < validFiles.length; i++) {
      setUploadProgress({ current: i + 1, total: validFiles.length });
      try {
        await RoomService.uploadRoomImage(id, validFiles[i]);
        successCount++;
      } catch (error) {
        toast.error(`"${validFiles[i].name}": ${uploadErrorMessage(error, 'upload failed')}`);
      }
    }

    // Re-fetch to get fresh presigned URLs for all images
    try {
      const data = await RoomService.getRoom(id);
      setImages(data.images || []);
    } catch (_) { /* room data will refresh on next navigation */ }

    if (successCount > 0) {
      toast.success(`${successCount} image${successCount === 1 ? '' : 's'} uploaded`);
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    // The hook resets the input's value automatically after onFiles fires.
  };

  // OS-native file chooser for the room image upload buttons. All three
  // trigger buttons (header "Add Photos", inline "Add more" tile, empty-state
  // dropzone) share this single hidden input via the hook's triggerPicker.
  // See hooks/useFilePicker for why this matters on iOS.
  const { triggerPicker: openImagePicker, fileInputElement: imageFileInput } = useFilePicker({
    kind: 'image',
    multiple: true,
    onFiles: handleImageUpload,
    disabled: uploading,
  });

  const handleImageDelete = async () => {
    if (!deleteImageCandidate) return;
    deletingImageRef.current = true; // synchronous: blocks the same-tick onClose
    setDeletingImage(true);
    try {
      await RoomService.deleteRoomImage(id, deleteImageCandidate);
      toast.success('Image deleted');
      const data = await RoomService.getRoom(id);
      setImages(data.images || []);
    } catch (error) {
      toast.error(error.message || 'Failed to delete image');
    } finally {
      deletingImageRef.current = false;
      setDeletingImage(false);
      setDeleteImageCandidate(null);
    }
  };

  const handleSetPrimary = async (imageId) => {
    try {
      await RoomService.setRoomImagePrimary(id, imageId);
      toast.success('Primary image updated');
      const data = await RoomService.getRoom(id);
      setImages(data.images || []);
    } catch (error) {
      toast.error('Failed to set primary image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!formData.name.trim()) {
      setNameError('Room Name is required');
      toast.error('Room Name is required');
      return;
    }
    setNameError(null);
    setSaveErrors([]);
    setSaving(true);
    try {
      // Defensively drop the retired `dimensions` key so legacy rooms don't
      // re-persist it on save. It's hidden from the custom-properties list and
      // superseded by dedicated fields, so spreading formData.properties as-is
      // would otherwise silently round-trip a stale value.
      const cleanedProperties = { ...(formData.properties || {}) };
      delete cleanedProperties.dimensions;
      const submitData = {
        ...formData,
        name: sanitizeInput(formData.name, 128),
        properties: {
          ...cleanedProperties,
          notes: notes.trim() || null
        }
      };
      // house_id '' means a legacy orphan the user didn't re-anchor. Send
      // nothing rather than '': the backend rejects an explicit null with 400
      // ("every room must anchor to a house") and would 404 on an empty-string
      // id, so a bare `...formData` spread breaks saving any unmigrated room.
      // To detach a room from its house, archive the house instead.
      if (formData.house_id) {
        submitData.house_id = parseInt(formData.house_id, 10);
      } else {
        delete submitData.house_id;
      }
      await RoomService.updateRoom(id, submitData);
      toast.success("Room updated successfully");
      navigate(`/room/${id}`);
    } catch (error) {
      console.error("Failed to update room:", error);
      // Surface field-level Pydantic errors inline and use the server's
      // message (the old copy ignored error.message entirely).
      setSaveErrors((error.errors || []).map((entry) =>
        `${(entry.loc || []).join('.') || 'form'}: ${entry.msg}`));
      toast.error(error.message || "Failed to update room");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingState fullPage />;
  }

  if (loadError) {
    return (
      <ErrorState
        title="Error Loading Room"
        message={loadError}
        onRetry={() => { setLoadError(null); setLoading(true); fetchRoom(); }}
        action={{ label: 'Back to Room', onClick: () => navigate(`/room/${id}`) }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-main)] p-4 md:p-8 animate-fade-in">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(`/room/${id}`)}
          className="flex items-center text-[var(--text-secondary)] hover:text-[var(--text-main)] mb-6 transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Room
        </button>

        <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] shadow-sm p-6">
          <h1 className="text-2xl font-bold text-[var(--text-main)] mb-6">Edit Room</h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Core Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor={ROOM_FIELD_IDS.name} className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-2">Room Name</label>
                <input
                  id={ROOM_FIELD_IDS.name}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={128}
                  className="w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none transition-all"
                  required
                  onInput={() => nameError && setNameError(null)}
                  aria-invalid={Boolean(nameError)}
                />
                {nameError && <p className="mt-1 text-xs text-red-500">{nameError}</p>}
              </div>

              <div>
                <label htmlFor={ROOM_FIELD_IDS.floorLevel} className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-2">Floor Level</label>
                <select
                  id={ROOM_FIELD_IDS.floorLevel}
                  name="floor"
                  value={formData.floor}
                  onChange={handleChange}
                  className="w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none transition-all"
                >
                  <option value="">Select a floor...</option>
                  {withImportedOption(configOptions.floor_levels, formData.floor).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* House — shown for multi-house users, and always for a room
                  that LOADED without a house: an unanchored legacy room is
                  exactly the case the user needs to fix, however many houses
                  they own. Keyed off loadedWithoutHouse (captured once) rather
                  than formData.house_id, so picking a house doesn't unmount
                  the field mid-edit. */}
              {(houses.length > 1 || loadedWithoutHouse) && (
                <div>
                  <label htmlFor={ROOM_FIELD_IDS.house} className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-2">House</label>
                  <select
                    id={ROOM_FIELD_IDS.house}
                    name="house_id"
                    value={formData.house_id}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none transition-all"
                  >
                    {/* Offered only while the room is still unanchored, so the
                        user can see that state; it disappears once they pick a
                        real house, which is correct — there's no way back to
                        "no house" (the backend rejects null). Never submitted:
                        handleSubmit strips an empty value rather than sending
                        null. */}
                    {!formData.house_id && <option value="">No house assigned</option>}
                    {houses.map(h => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Room Images */}
            <div className="border-t border-[var(--border)] pt-6">
              <div className="flex items-center justify-between mb-4">
                <label className="text-sm font-semibold text-[var(--text-main)] flex items-center">
                  <Camera size={16} className="mr-2 text-[var(--primary)]" />
                  Room Images
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[var(--text-muted)]">
                    {images.length}/{MAX_ROOM_IMAGES}
                  </span>
                  {images.length < MAX_ROOM_IMAGES && (
                    <button
                      type="button"
                      onClick={openImagePicker}
                      disabled={uploading}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                    >
                      <ImagePlus size={14} />
                      Add Photos
                    </button>
                  )}
                  {imageFileInput}
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="mb-4 p-3 bg-[var(--bg-hover)] rounded-lg border border-[var(--border)]">
                  <div className="flex items-center gap-3">
                    <Loader2 size={16} className="animate-spin text-[var(--primary)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[var(--text-main)]">
                        Uploading {uploadProgress.current} of {uploadProgress.total}...
                      </div>
                      <div className="mt-1.5 h-1.5 bg-[var(--border)] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[var(--primary)] rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Image Grid */}
              {images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {images.map((image) => (
                    <div
                      key={image.id}
                      className={`relative group rounded-lg overflow-hidden border-2 transition-colors bg-[var(--bg-hover)] ${
                        image.is_primary ? 'border-[var(--primary)]' : 'border-[var(--border)]'
                      }`}
                      onClick={() => setActiveImageMenu(prev => prev === image.id ? null : image.id)}
                    >
                      <img
                        src={image.url || `/static/uploads/rooms/${image.filename}`}
                        alt={image.original_filename || 'Room image'}
                        className="w-full h-28 object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = PLACEHOLDER_IMAGES.room;
                        }}
                      />

                      {/* Primary badge */}
                      {image.is_primary && (
                        <span className="absolute top-1.5 left-1.5 bg-[var(--primary)] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-1">
                          <Star size={10} fill="currentColor" /> Primary
                        </span>
                      )}

                      {/* Action overlay — visible on hover (desktop) or tap (mobile) */}
                      <div className={`absolute inset-0 bg-black/50 flex items-center justify-center gap-2 transition-opacity ${activeImageMenu === image.id ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto'}`}>
                        {!image.is_primary && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleSetPrimary(image.id); }}
                            disabled={uploading}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/95 text-amber-600 hover:bg-white text-xs font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Set as primary"
                          >
                            <Star size={12} /> Primary
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDeleteImageCandidate(image.id); }}
                          disabled={uploading}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/95 text-red-600 hover:bg-white text-xs font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete image"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Inline add-more tile when there are existing images and room for more */}
                  {images.length < MAX_ROOM_IMAGES && !uploading && (
                    <button
                      type="button"
                      onClick={openImagePicker}
                      className="flex flex-col items-center justify-center h-28 rounded-lg border-2 border-dashed border-[var(--border)] hover:border-[var(--primary)] hover:bg-[var(--bg-hover)] transition-all text-[var(--text-muted)] hover:text-[var(--primary)]"
                    >
                      <ImagePlus size={20} />
                      <span className="text-xs mt-1.5">Add more</span>
                    </button>
                  )}
                </div>
              ) : !uploading ? (
                /* Empty state — drop zone style */
                <button
                  type="button"
                  onClick={openImagePicker}
                  className="w-full flex flex-col items-center justify-center p-8 border-2 border-dashed border-[var(--border)] rounded-lg hover:border-[var(--primary)] hover:bg-[var(--bg-hover)] transition-all"
                >
                  <Upload size={28} className="text-[var(--text-muted)] mb-2" />
                  <span className="text-sm font-medium text-[var(--text-secondary)]">
                    Click to upload photos
                  </span>
                  <span className="text-xs text-[var(--text-muted)] mt-1">
                    Select multiple files &middot; PNG, JPG, GIF, or WebP up to {MAX_IMAGE_SIZE_MB}MB each
                  </span>
                </button>
              ) : null}

              {images.length >= MAX_ROOM_IMAGES && (
                <p className="text-xs text-[var(--text-muted)] text-center mt-3">
                  Maximum of {MAX_ROOM_IMAGES} images reached. Delete an image to add a new one.
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="border-t border-[var(--border)] pt-6">
              <label className="block text-sm font-semibold text-[var(--text-main)] mb-4 flex items-center">
                <FileText size={16} className="mr-2 text-[var(--primary)]" />
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this room..."
                rows={3}
                maxLength={2000}
                className="w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none transition-all resize-y"
              />
            </div>

            {/* Custom Properties */}
            <div className="border-t border-[var(--border)] pt-6">
              <label className="block text-sm font-semibold text-[var(--text-main)] mb-4">Custom Properties</label>

              {/* Existing Properties List */}
              {Object.entries(formData.properties).filter(([key, value]) => key !== 'dimensions' && key !== 'notes' && typeof value !== 'object').length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {Object.entries(formData.properties).filter(([key, value]) => key !== 'dimensions' && key !== 'notes' && typeof value !== 'object').map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center bg-[var(--bg-hover)] p-3 rounded-lg border border-[var(--border)]">
                      <div className="overflow-hidden">
                        <span className="font-medium text-[var(--text-main)] block text-sm">{key}</span>
                        <span className="text-[var(--text-secondary)] text-sm">{value}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeProperty(key)}
                        className="text-[var(--text-muted)] hover:text-red-500 p-2 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Property Inputs */}
              <div className="flex flex-col md:flex-row gap-3 items-end bg-[var(--bg-hover)] p-4 rounded-lg border border-[var(--border)]">
                <div className="flex-1 w-full">
                  <label className="block text-xs text-[var(--text-secondary)] mb-1">Property Name</label>
                  <input
                    value={newPropKey}
                    onChange={(e) => setNewPropKey(e.target.value)}
                    placeholder="e.g. Ceiling Height"
                    maxLength={100}
                    className="w-full p-2 rounded bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-main)] text-sm focus:outline-none focus:border-[var(--primary)]"
                  />
                </div>
                <div className="flex-1 w-full">
                  <label className="block text-xs text-[var(--text-secondary)] mb-1">Value</label>
                  <input
                    value={newPropValue}
                    onChange={(e) => setNewPropValue(e.target.value)}
                    placeholder="e.g. 9ft"
                    maxLength={255}
                    className="w-full p-2 rounded bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-main)] text-sm focus:outline-none focus:border-[var(--primary)]"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addProperty();
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={addProperty}
                  disabled={!newPropKey.trim() || !newPropValue.trim()}
                  className="w-full md:w-auto px-4 py-2 bg-[var(--primary)] text-white rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity text-sm font-medium"
                  style={{ height: '38px' }}
                >
                  Add
                </button>
              </div>
            </div>

            {saveErrors.length > 0 && (
              <div className="p-4 bg-[var(--error-container)] text-[var(--error)] rounded-xl text-sm space-y-1" role="alert">
                {saveErrors.map((line) => <p key={line}>{line}</p>)}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 mt-6 border-t border-[var(--border)]">
              <button
                type="button"
                onClick={() => navigate(`/room/${id}`)}
                disabled={saving}
                className="px-6 py-2 text-[var(--text-secondary)] hover:text-[var(--text-main)] font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all font-medium flex items-center shadow-lg shadow-[color-mix(in_srgb,var(--primary),transparent_80%)]"
              >
                {saving ? <Loader2 size={18} className="animate-spin mr-2" /> : <Save size={18} className="mr-2" />}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteImageCandidate !== null}
        onClose={() => { if (!deletingImageRef.current) setDeleteImageCandidate(null); }}
        onConfirm={handleImageDelete}
        title="Delete Image"
        message="Delete this image? This cannot be undone."
        confirmText="Delete"
        isDanger
        isLoading={deletingImage}
        icon={Trash2}
      />
    </div>
  );
};

export default RoomEdit;
